import { ModuleHeader, SpinnerWithBackground, TabBar } from '@viapeople/components'
import { useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useEnpsStore } from './store'

/**
 * @fileoverview Manage eNPS Page
 *
 * Main page for creating/editing eNPS configurations with tab navigation
 */

const tabs = [
	{ id: 'general', label: 'General' },
	{ id: 'configuration', label: 'Configuration' },
	{ id: 'schedule', label: 'Schedule' },
	{ id: 'population', label: 'Population' },
	{ id: 'email-template', label: 'Email Template' },
	{ id: 'viewers', label: 'Viewers' },
	{ id: 'launch', label: 'Launch' },
	{ id: 'results', label: 'Results' },
]

export const ManageEnpsPage = () => {
	const { organizationId, enpsId } = useParams<{ organizationId: string; enpsId: string }>()

	const {
		organization,
		enpsConfig,
		isLoading,
		isLoadingOrganization,
		fetchOrganization,
		fetchEnpsConfig,
		initializeNewConfig,
	} = useEnpsStore()

	const navigate = useNavigate()
	const location = useLocation()
	const pathArray = location.pathname.split('/')
	const currentTab = pathArray?.[5] ?? 'general'

	// Fetch data from store on mount
	useEffect(() => {
		if (organizationId) {
			fetchOrganization(organizationId)

			if (enpsId && enpsId !== 'create') {
				fetchEnpsConfig(enpsId, organizationId)
			} else {
				initializeNewConfig(organizationId)
			}
		}
	}, [organizationId, enpsId, fetchOrganization, fetchEnpsConfig, initializeNewConfig])

	const handleTabChange = (tabId: string) => {
		const enpsPath = enpsId === 'create' || !enpsId ? 'create' : enpsId
		navigate(`/organizations/${organizationId}/enps/${enpsPath}/${tabId}`)
	}

	// Available tabs based on creation status
	const availableTabs = useMemo(() => {
		if (!enpsId || enpsId === 'create') {
			// Only show General tab for new configs
			return [tabs[0]]
		}
		return tabs
	}, [enpsId])

	if (isLoadingOrganization || isLoading) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	return (
		<main className='w-full p-6 min-h-full'>
			<div className='gap-6 flex flex-col'>
				<ModuleHeader
					breadcrumb={[
						{ label: 'Organizations', to: '/organizations' },
						{ label: organization?.name || '', to: `/organizations/${organizationId}` },
						{ label: 'NPS', to: `/organizations/${organizationId}/enps` },
						{
							label: enpsConfig?.name || (enpsId && enpsId !== 'create' ? 'Manage NPS' : 'Create NPS'),
						},
					]}
					title={enpsConfig?.name || (enpsId && enpsId !== 'create' ? 'Manage NPS' : 'Create NPS Survey')}
					logo={{
						url: organization?.logo || '',
						name: organization?.name || '',
					}}
				/>
				<TabBar tabs={availableTabs} activeTab={currentTab} onChange={handleTabChange} />
			</div>
			<Outlet context={{ organization, enpsConfig }} />
		</main>
	)
}
